// Chakra imports
import React, { useState } from "react";
import { useEffect } from "react";
import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { useSetRecoilState } from "recoil";
import { useParams } from "react-router-dom";
import { MdPeople, MdAttachMoney, MdLocationOn } from "react-icons/md";

import { deleteModalState } from "atoms/deleteModalState";
import { userProfilesTableHeaders } from "./variables/tableHeaders";
import UserProfilesTable from "./components/UserProfilesTable";
import UserProfileTabs from "./tabs/UserProfileTabs";
import useAxios from "axios-hooks";

const UserProfilesList = () => {
  const setDeleteModal = useSetRecoilState(deleteModalState);
  const infoBgColor = useColorModeValue("rgba(255, 255, 255, 0.8)", "rgba(26, 32, 44, 0.8)");
  const infoBorderColor = useColorModeValue("rgba(255, 255, 255, 0.5)", "rgba(255, 255, 255, 0.1)");
  const headingColor = useColorModeValue("gray.800", "white");
  const cardBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const cardBorderColor = useColorModeValue("gray.100", "whiteAlpha.100");
  const labelColor = useColorModeValue("gray.700", "white");
  const tipBg = useColorModeValue("brand.50", "rgba(35, 169, 121, 0.08)");
  const tipTextColor = useColorModeValue("brand.700", "brand.200");

  const [{ data, loading: listLoading, error }, getUserProfiles] = useAxios(
    { method: "GET", url: `/api/userprofiles` },
    { manual: true }
  );

  const [tableData, setTableData] = useState([]);

  const fetchUserProfiles = async () => {
    try {
      const response = await getUserProfiles();
      setTableData(response.data.data);
    } catch (error) {
      if (error?.code === "ERR_CANCELED") {
        return;
      }
      console.error("Error fetching user groups:", error);
    }
  };

  useEffect(() => {
    fetchUserProfiles();
  }, []);

  // Otherwise, show the list view
  return (
    <>
      <Box
        bg={infoBgColor}
        backdropFilter="blur(10px)"
        borderRadius="2xl"
        boxShadow="xl"
        border="1px solid"
        borderColor={infoBorderColor}
        p={6}
        mb={6}
      >
        <VStack align="start" spacing={4}>
          <Flex align="center" gap={3}>
            <Flex
              align="center"
              justify="center"
              w="42px"
              h="42px"
              borderRadius="xl"
              bg="rgba(35, 169, 121, 0.12)"
            >
              <Icon as={MdPeople} boxSize={6} color="brand.500" />
            </Flex>
            <Box>
              <Text fontSize="lg" fontWeight="bold" color={headingColor}>
                What are User Groups?
              </Text>
              <Text fontSize="sm" color="gray.500">
                {/* private groups of users for separate pricing and to control private location access */}
                These groups will have access to customized pricing and also can access private location if made available
              </Text>
            </Box>
          </Flex>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="100%">
            <HStack
              align="start"
              spacing={3}
              bg={cardBg}
              p={4}
              borderRadius="lg"
              border="1px solid"
              borderColor={cardBorderColor}
            >
              <Flex
                align="center"
                justify="center"
                w="32px"
                h="32px"
                borderRadius="lg"
                bg="blue.50"
                flexShrink={0}
                mt={0.5}
              >
                <Icon as={MdPeople} boxSize={4} color="blue.500" />
              </Flex>
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color={labelColor}>
                  Users
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Manage users from the Users tab.
                </Text>
              </Box>
            </HStack>

            <HStack
              align="start"
              spacing={3}
              bg={cardBg}
              p={4}
              borderRadius="lg"
              border="1px solid"
              borderColor={cardBorderColor}
            >
              <Flex
                align="center"
                justify="center"
                w="32px"
                h="32px"
                borderRadius="lg"
                bg="green.50"
                flexShrink={0}
                mt={0.5}
              >
                <Icon as={MdAttachMoney} boxSize={4} color="green.500" />
              </Flex>
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color={labelColor}>
                  Private Pricing
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Set custom charging rates per charger for this group. Configure pricing in the Private Pricing tab.
                </Text>
              </Box>
            </HStack>

            <HStack
              align="start"
              spacing={3}
              bg={cardBg}
              p={4}
              borderRadius="lg"
              border="1px solid"
              borderColor={cardBorderColor}
            >
              <Flex
                align="center"
                justify="center"
                w="32px"
                h="32px"
                borderRadius="lg"
                bg="brand.50"
                flexShrink={0}
                mt={0.5}
              >
                <Icon as={MdLocationOn} boxSize={4} color="brand.500" />
              </Flex>
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color={labelColor}>
                  Private Locations
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Grant access to private charging locations. Manage in the Private Locations tab.
                </Text>
              </Box>
            </HStack>
          </SimpleGrid>

          <Flex
            bg={tipBg}
            borderRadius="lg"
            p={3}
            w="100%"
            align="center"
            gap={2}
          >
            <Badge colorScheme="brand" borderRadius="full" px={2} fontSize="2xs">
              TIP
            </Badge>
            <Text fontSize="xs" color={tipTextColor}>
              Create a group below, then open it to configure users, pricing, and location access from the respective tabs.
            </Text>
          </Flex>
        </VStack>
      </Box>

      <UserProfilesTable
        columnsData={userProfilesTableHeaders}
        data={tableData}
        onRefresh={fetchUserProfiles}
      />
    </>
  );
};

export default UserProfilesList;
